import { removeVowelPoints, removeNonLetters } from '../../utils/text-cleaning.utils';
import { matchWord, maxEditsForMatch } from '../../utils/word-distance';
export interface WordTiming {
  /** Word start time in seconds (relative to the audio segment). */
  start_time: number;
  /** Word end time in seconds (relative to the audio segment). */
  end_time: number;
  /**
   * Speechmatics' own confidence in the word it transcribed (0.0–1.0). Not a
   * timing — it rides along here so it lands on the same word it was heard for.
   * Undefined when the segment window was used instead of a per-word result.
   */
  confidence?: number;
}

export interface IncrementalAlignResult {
  /** Score per correct word in [cursor, newCursor). Index 0 = correct[cursor]. */
  scores: number[];
  /** Matched spoken word per correct word position. Empty string = unmatched. */
  candidate_words: string[];
  /** Next cursor position in correctWords after this segment. */
  newCursor: number;
  /** Per-position word timing (parallel to scores/candidate_words). null when timing is unavailable or the word was unmatched. */
  wordTimes: (WordTiming | null)[];
  /**
   * Reading errors between the spoken word and the text word, per position:
   * 0 is an exact match after spelling is normalised, a half is a letter that
   * sounds the same, 1 is one real error. null where nothing was matched.
   */
  edits: (number | null)[];
}

/**
 * Incrementally aligns a single Final segment's words to correctWords[cursor:].
 *
 * Only called on Final events. Walks finalWords left-to-right and greedily
 * matches each to the closest correct word within a 4-word lookahead window,
 * preserving reading order. Closeness is the weighted edit distance between
 * the two words' skeletons (word-distance.ts), so the recogniser's spelling
 * habits cost nothing and a wrong letter costs one; the old bigram score is
 * gone, it could not tell ליבך from לבך and passed קודש for חודש.
 *
 * A spoken word is taken as an attempt at a text word only within
 * maxEditsForMatch of it: one error on a short word, two on a longer one.
 * Beyond that it is not that word at all, and the aligner keeps looking.
 *
 * scores[i] is for correctWords[cursor + i], 1 for identical skeletons and
 * falling with the edits; edits[i] carries the count itself, which is what
 * the verdict is made from. 0 in scores and null in edits mean unmatched.
 */
export function incrementalAlign(
  correct: string[],
  finalWords: string[],
  cursor: number,
  finalWordTimes?: (WordTiming | null)[],
  equivalents?: (string[] | undefined)[],
  pointed?: (string | undefined)[],
): IncrementalAlignResult {
  const MAX_LOOKAHEAD = 4;

  let correctCursor = cursor;
  const scoreMap = new Map<number, number>();
  const editMap = new Map<number, number>();
  const wordMap = new Map<number, string>();
  const timeMap = new Map<number, WordTiming | null>();

  for (let spkIdx = 0; spkIdx < finalWords.length; spkIdx++) {
    const rawSpoken = finalWords[spkIdx];
    if (correctCursor >= correct.length) break;

    const spokenWord = removeNonLetters(removeVowelPoints(rawSpoken));
    if (!spokenWord) continue;

    let bestEdits = Infinity;
    let bestScore = 0;
    let bestPos = -1;

    const end = Math.min(correctCursor + MAX_LOOKAHEAD + 1, correct.length);
    for (let i = correctCursor; i < end; i++) {
      // A word may be written one way and said another: the Divine Name, and
      // the substitutions beside it. Score the spoken word against the written
      // form and against every accepted reading of it, and keep the best. The
      // decision is made per position, so two readers, or one reader who
      // varies, are both matched where they actually read.
      const candidates = [correct[i], ...(equivalents?.[i] ?? [])];
      const { edits, similarity } = matchWord(spokenWord, candidates, pointed?.[i]);
      if (edits > maxEditsForMatch(correct[i])) continue;
      // Strictly closer wins; a tie goes to the earlier word, which keeps a
      // reader who says a word twice from being pulled forward.
      if (edits < bestEdits) {
        bestEdits = edits;
        bestScore = similarity;
        bestPos = i;
      }
    }

    if (bestPos !== -1) {
      scoreMap.set(bestPos, bestScore);
      editMap.set(bestPos, bestEdits);
      wordMap.set(bestPos, rawSpoken);
      timeMap.set(bestPos, finalWordTimes?.[spkIdx] ?? null);
      correctCursor = bestPos + 1;
    }
  }

  const rangeLen = correctCursor - cursor;
  const scores = new Array<number>(rangeLen).fill(0);
  const candidate_words = new Array<string>(rangeLen).fill('');
  const wordTimes = new Array<WordTiming | null>(rangeLen).fill(null);
  const edits = new Array<number | null>(rangeLen).fill(null);

  for (const [pos, score] of scoreMap) {
    const rel = pos - cursor;
    scores[rel] = score;
    edits[rel] = editMap.get(pos) ?? null;
    candidate_words[rel] = wordMap.get(pos)!;
    wordTimes[rel] = timeMap.get(pos) ?? null;
  }

  return { scores, candidate_words, newCursor: correctCursor, wordTimes, edits };
}
